import type { SiteMapPath, SiteMapPathType, SiteSurvey } from '../../types';
import { effectiveChosenId } from './placementOptions';
import { calculatePathDistanceFt } from './siteMapGeometry';

/** One run of the chosen location: the route it came from and its length in feet. */
export interface ChosenRun {
  pathId: string;
  lengthFt: number;
  /** `tape` when the surveyor typed a tape length, else `map` (length of the drawn points). */
  source: 'tape' | 'map';
  /**
   * The length stored with the route disagrees with its drawn points (the server
   * trusts the stored number and never recomputes it, so a stale one survives a
   * moved point). The map length used here is the one recomputed from the points.
   */
  storedLengthStale: boolean;
  /** Length of the drawn points in whole feet (null when the route has fewer than two points). */
  drawnFt: number | null;
  /** The length stored with the route, which the site-visit PDF and the AI facts print (null when absent). */
  storedFt: number | null;
}

export interface ChosenRunLengths {
  electrical: ChosenRun | null;
  gas: ChosenRun | null;
  /** A location was chosen, but the id names no option any more (deleted elsewhere); rank 1 was used. */
  chosenOptionMissing: boolean;
}

const ELECTRICAL_TYPES: readonly SiteMapPathType[] = ['conduit', 'trenching'];
const GAS_TYPES: readonly SiteMapPathType[] = ['gas_pipe'];

/** A stored length this far from the drawn points is stale, not rounding. */
const STALE_MIN_FT = 5;
const STALE_RATIO = 0.1;

/**
 * A single conduit or gas run longer than this is not a usable length: it reads as "no length", the same as
 * a missing route, so a typo (a tape of 1e9) or a far-off point can never price a quote. Applies to the
 * tape, stored and drawn figures alike.
 */
export const MAX_USABLE_RUN_FT = 5000;

const positive = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 && n <= MAX_USABLE_RUN_FT ? n : null;
};

const validPoint = (p: { lat?: unknown; lng?: unknown } | undefined): boolean =>
  Number.isFinite(p?.lat) && Number.isFinite(p?.lng) && Math.abs(p!.lat as number) <= 90 && Math.abs(p!.lng as number) <= 180;

function drawnLengthFt(path: SiteMapPath): number | null {
  const points = (path.points ?? []).filter(validPoint);
  return points.length >= 2 ? positive(calculatePathDistanceFt(points)) : null;
}

function runOf(paths: readonly SiteMapPath[], pathId: string | undefined, kinds: readonly SiteMapPathType[]): ChosenRun | null {
  if (!pathId) return null;
  const path = paths.find((p) => p.id === pathId);
  if (!path || !kinds.includes(path.type)) return null;
  const drawn = drawnLengthFt(path);
  const stored = positive(path.measuredDistanceFt);
  const storedLengthStale =
    drawn !== null && stored !== null && Math.abs(drawn - stored) > Math.max(STALE_MIN_FT, drawn * STALE_RATIO);
  const tape = positive(path.overrideDistanceFt);
  if (tape !== null) return { pathId, lengthFt: tape, source: 'tape', storedLengthStale, drawnFt: drawn, storedFt: stored };
  const map = drawn ?? stored;
  return map !== null ? { pathId, lengthFt: map, source: 'map', storedLengthStale, drawnFt: drawn, storedFt: stored } : null;
}

/**
 * The electrical and gas run lengths of the location chosen for the permit
 * (docs/plans/permit-package/site-survey-permit-fields.md section 1a): the
 * chosen option, else rank 1; its own named routes only, never the longest
 * route on the map (with backups, that can belong to a location not chosen);
 * the tape length over the map length, and the map length measured from the
 * drawn points rather than the stored number. A route the option does not
 * name, of the wrong kind, or with no length is null: a missing answer, never
 * a guess.
 */
export function chosenRunLengths(survey: Pick<SiteSurvey, 'placementOptions' | 'chosenPlacementOptionId' | 'siteMap'>): ChosenRunLengths {
  const options = survey.placementOptions ?? [];
  const named = survey.chosenPlacementOptionId;
  const chosenOptionMissing = Boolean(named) && options.length > 0 && !options.some((o) => o.id === named);
  const chosenId = effectiveChosenId(options, named);
  const option = chosenId ? options.find((o) => o.id === chosenId) : undefined;
  if (!option) return { electrical: null, gas: null, chosenOptionMissing };
  const paths = survey.siteMap?.paths ?? [];
  return {
    electrical: runOf(paths, option.electricalPathId, ELECTRICAL_TYPES),
    gas: runOf(paths, option.gasPathId, GAS_TYPES),
    chosenOptionMissing,
  };
}
